import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SettlementView as SharedSettlementView } from "@tabipla/components";
import type { SettlementWithPaymentDto, MemberType } from "@tabipla/shared";
import {
  useSettlementsWithPayments,
  useCreatePayment,
  useMarkPaymentPaid,
  useConfirmPayment,
} from "@/application/hooks/usePayments";

interface SettlementViewProps {
  groupId: string;
  sessionId: string;
  currentUserId: string;
  currentUserType: MemberType;
  canEdit: boolean;
}

export function SettlementView({
  groupId,
  sessionId,
  currentUserId,
  currentUserType,
  canEdit,
}: SettlementViewProps) {
  const { t } = useTranslation();
  const { data: settlements = [], isLoading } = useSettlementsWithPayments(groupId, sessionId);

  const createPaymentMutation = useCreatePayment(groupId, sessionId);
  const markPaidMutation = useMarkPaymentPaid(groupId, sessionId);
  const confirmMutation = useConfirmPayment(groupId, sessionId);

  const handleMarkPaid = async (settlement: SettlementWithPaymentDto) => {
    try {
      if (settlement.paymentId) {
        await markPaidMutation.mutateAsync(settlement.paymentId);
      } else {
        const payment = await createPaymentMutation.mutateAsync({
          fromMemberId: settlement.fromMemberId,
          fromMemberType: settlement.fromMemberType,
          toMemberId: settlement.toMemberId,
          toMemberType: settlement.toMemberType,
          amount: settlement.amount,
          currency: settlement.currency,
        });
        if (payment) {
          await markPaidMutation.mutateAsync(payment.id);
        }
      }
      toast.success(t("payments.markedAsPaid", "支払い完了を報告しました"));
    } catch {
      toast.error(t("payments.markPaidError", "エラーが発生しました"));
    }
  };

  const handleConfirm = async (paymentId: string) => {
    try {
      await confirmMutation.mutateAsync(paymentId);
      toast.success(t("payments.confirmed", "受け取りを確認しました"));
    } catch {
      toast.error(t("payments.confirmError", "エラーが発生しました"));
    }
  };

  return (
    <SharedSettlementView
      settlements={settlements}
      isLoading={isLoading}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
      canEdit={canEdit}
      onMarkPaid={handleMarkPaid}
      isMarkingPaid={markPaidMutation.isPending || createPaymentMutation.isPending}
      onConfirmReceipt={handleConfirm}
      isConfirming={confirmMutation.isPending}
      variant="avatar"
      showPayPay={false}
    />
  );
}
